import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Tabs,
  Tab,
  useTheme
} from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";

interface DailyDataItem {
  date: string;
  btcRewardReceived: number;
  btcRewardReceivedUsd: number;
  btcPrice: number;
  hostingCostReceivedUsd: number;
  hostingCostReceivedBtc: number;
  difficulty: number;
  treasuryBalance: number;
  treasuryBalanceBtc: number;
}

interface MiningResult {
  btcAmountPaidToCustomer: number;
  btcAmountPaidToCustomerInUsd: number;
  hostingRevenueUsd: number;
  machinePriceUsd: number;
  hostingRevenueInBtc: number;
  intialBtcInTreasury: number;
  finalBtcInTreasury: number;
  daysMined: number;
  endDate: string;
  dailyData: DailyDataItem[];
}

interface MiningResultModalProps {
  open: boolean;
  onClose: () => void;
  result: MiningResult | null;
}

export default function MiningResultModal({ open, onClose, result }: MiningResultModalProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [tabIndex, setTabIndex] = useState(0);

  if (!result) return null;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const downloadCSV = () => {
    if (!result?.dailyData) return;

    const headers = [
      "Date",
      "BTC Reward",
      "BTC Reward (USD)",
      "BTC Price",
      "Hosting Cost (USD)",
      "Hosting Cost (BTC)",
      "Difficulty",
      "Treasury Balance",
      "Treasury Balance BTC"
    ];

    const csvContent = [
      headers.join(","),
      ...result.dailyData.map(item => [
        `"${new Date(item.date).toLocaleDateString()}"`,
        item.btcRewardReceived.toFixed(6),
        item.btcRewardReceivedUsd.toFixed(2),
        item.btcPrice.toFixed(2),
        item.hostingCostReceivedUsd.toFixed(2),
        item.hostingCostReceivedBtc.toFixed(6),
        item.difficulty.toExponential(),
        item.treasuryBalance.toFixed(2),
        item.treasuryBalanceBtc.toFixed(6)
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `mining_daily_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const cardBg = isDark ? "#1E1E1E" : "#ffffff";
  const cardBorder = isDark ? "#333" : "#f0f0f0";
  const cardHoverShadow = (color: string) => (isDark ? `0 4px 14px ${color}40` : `0 4px 12px ${color}20`);

  const summaryBg = isDark ? "#1b3320" : "#e8f5e9";
  const summaryBorder = isDark ? "#264d33" : "#c8e6c9";
  const summaryText = isDark ? "#9deab0" : "#388e3c";

  const fields = [
    { label: "BTC Paid to Customer", value: result.btcAmountPaidToCustomer.toFixed(6), unit: "BTC", color: "#2E7D32", icon: "₿" },
    { label: "BTC Paid to Customer (USD)", value: result.btcAmountPaidToCustomerInUsd.toFixed(2), unit: "$", color: "#1976D2", icon: "💵" },
    { label: "Hosting Revenue (USD)", value: result.hostingRevenueUsd.toFixed(2), unit: "$", color: "#ED6C02", icon: "🏢" },
    { label: "Hosting Revenue (BTC)", value: result.hostingRevenueInBtc.toFixed(6), unit: "BTC", color: "#FF5722", icon: "₿" },
    { label: "Machine Price", value: result.machinePriceUsd.toFixed(2), unit: "$", color: "#9C27B0", icon: "⚙️" },
    { label: "Initial BTC in Treasury", value: result.intialBtcInTreasury.toFixed(6), unit: "BTC", color: "#4CAF50", icon: "🏦" },
    { label: "Final BTC in Treasury", value: result.finalBtcInTreasury.toFixed(6), unit: "BTC", color: "#8BC34A", icon: "💰" },
    { label: "Days Before Unprofitable", value: result.daysMined.toString(), unit: "days", color: "#D32F2F", icon: "📅" },
    { label: "Mining End Date", value: new Date(result.endDate).toLocaleDateString(), unit: "", color: "#1565C0", icon: "⏰" }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: isDark ? "#121212" : "#fff",
          boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.6)" : "0 8px 32px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", py: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: isDark ? "#e0e0e0" : "#2C3E50" }}>
          Mining Simulation Results
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? "#9e9e9e" : "#6C757D", mt: 0.5 }}>
          Optimal pricing analysis based on your parameters
        </Typography>
      </DialogTitle>

      <Tabs value={tabIndex} onChange={handleTabChange} centered>
        <Tab label="Overall Result" />
        <Tab label="Daily Data" />
      </Tabs>

      <DialogContent sx={{ py: 3 }}>
        {/* Overall Result Tab */}
        {tabIndex === 0 && (
          <Box>
            <Grid container spacing={3}>
              {fields.map((field) => (
                <Grid item xs={12} sm={6} md={4} key={field.label}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: cardBg,
                      border: `1px solid ${cardBorder}`,
                      transition: "0.2s",
                      height: "100%",
                      "&:hover": {
                        borderColor: field.color,
                        boxShadow: cardHoverShadow(field.color),
                        transform: "translateY(-2px)",
                      },
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      mt: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: `${field.color}25`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1.5,
                        }}
                      >
                        <Typography sx={{ fontSize: "1rem", color: field.color }}>{field.icon}</Typography>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? "#e0e0e0" : "#424242",
                          fontSize: "0.8rem",
                          letterSpacing: 0.5,
                        }}
                      >
                        {field.label}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end" }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: field.color,
                          fontSize: "1.75rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {field.value}
                        {field.unit && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.9rem",
                              color: isDark ? "#bdbdbd" : "#757575",
                              fontWeight: 500,
                              ml: 0.5,
                            }}
                          >
                            {field.unit}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Summary Section */}
            <Box
              sx={{
                mt: 4,
                p: 3,
                backgroundColor: summaryBg,
                borderRadius: 3,
                border: `1px solid ${summaryBorder}`,
                boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.4)" : "0 2px 12px rgba(0,0,0,0.05)"
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: summaryText,
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                  fontSize: "1rem",
                }}
              >
                💡 Key Insight
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: summaryText,
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                }}
              >
                Based on the simulation, the machine remains profitable for{" "}
                <strong>{result.daysMined} days</strong>.
                The optimal machine price is <strong>${result.machinePriceUsd.toFixed(2)}</strong>.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Daily Data Tab */}
        {tabIndex === 1 && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? "#e0e0e0" : "#2C3E50" }}>
                Daily Mining Data
              </Typography>
              <Button
                onClick={downloadCSV}
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={{
                  borderColor: isDark ? "#444" : "#d0d0d0",
                  color: isDark ? "#e0e0e0" : "#424242",
                  "&:hover": {
                    borderColor: isDark ? "#666" : "#1976d2",
                    backgroundColor: isDark ? "#333" : "#f5f5f5",
                  },
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Download CSV
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>BTC Reward</TableCell>
                    <TableCell>BTC Reward (USD)</TableCell>
                    <TableCell>BTC Price</TableCell>
                    <TableCell>Hosting Cost (USD)</TableCell>
                    <TableCell>Hosting Cost (BTC)</TableCell>
                    <TableCell>Difficulty</TableCell>
                    <TableCell>Treasury Balance</TableCell>
                    <TableCell>Treasury Balance BTC</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.dailyData.map((item) => (
                    <TableRow key={item.date}>
                      <TableCell>{item.date}</TableCell>
                      <TableCell>{item.btcRewardReceived.toFixed(6)}</TableCell>
                      <TableCell>{item.btcRewardReceivedUsd.toFixed(2)}</TableCell>
                      <TableCell>{item.btcPrice.toFixed(2)}</TableCell>
                      <TableCell>{item.hostingCostReceivedUsd.toFixed(2)}</TableCell>
                      <TableCell>{item.hostingCostReceivedBtc.toFixed(6)}</TableCell>
                      <TableCell>{item.difficulty.toExponential()}</TableCell>
                      <TableCell>{item.treasuryBalance.toFixed(2)}</TableCell>
                      <TableCell>{item.treasuryBalanceBtc.toFixed(6)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: isDark ? "#444" : "#6C757D",
            "&:hover": { backgroundColor: isDark ? "#555" : "#5A6268" },
            borderRadius: 1,
            px: 4,
            py: 1,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
